const supportedLanguageCodes = ['en', 'es', 'fr', 'de', 'it', 'pt', 'ro', 'ru', 'ja', 'ko', 'zh', 'ar', 'hi', 'da'];

const nativeLanguageNames = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  pt: 'Português',
  ro: 'Română',
  ru: 'Русский',
  ja: '日本語',
  ko: '한국어',
  zh: '中文',
  ar: 'العربية',
  hi: 'हिन्दी',
  da: 'Dansk'
};

/**
 * Service to detect and manage user language preferences
 */
export const languageService = {
  /**
   * Get user's preferred language
   * @returns {string} Language code (e.g., 'en', 'es', 'ja')
   */
  getLanguage() {
    // Check if user has set a language preference
    const storedLanguage = localStorage.getItem('cognimates_copilot_language');
    
    if (storedLanguage) {
      const storedBaseLanguage = storedLanguage.split('-')[0];
      return supportedLanguageCodes.includes(storedBaseLanguage) ? storedBaseLanguage : 'en';
    }
    
    // Detect from browser
    const browserLanguages = navigator.languages || [navigator.language];
    const primaryLanguage = browserLanguages[0]?.split('-')[0] || 'en';
    const supportedLanguage = supportedLanguageCodes.includes(primaryLanguage) ? primaryLanguage : 'en';
    
    // Store for future use
    this.setLanguage(supportedLanguage);
    
    return supportedLanguage;
  },
  
  /**
   * Set user's preferred language
   * @param {string} languageCode - ISO language code
   */
  setLanguage(languageCode) {
    const baseLanguageCode = languageCode?.split('-')[0] || 'en';
    localStorage.setItem(
      'cognimates_copilot_language',
      supportedLanguageCodes.includes(baseLanguageCode) ? baseLanguageCode : 'en'
    );
  },
  
  /**
   * Get display name for a language code in its native form
   * @param {string} languageCode - ISO language code
   * @returns {string} Language name in native form
   */
  getLanguageDisplayName(languageCode) {
    const baseLanguageCode = languageCode?.split('-')[0] || 'en';

    try {
      if (nativeLanguageNames[baseLanguageCode]) {
        return nativeLanguageNames[baseLanguageCode];
      }
      
      // If not in our predefined list, try browser API
      return new Intl.DisplayNames([baseLanguageCode], { type: 'language' })
        .of(baseLanguageCode);
    } catch (error) {
      // Fallback for older browsers that don't support Intl.DisplayNames
      return nativeLanguageNames[baseLanguageCode] || baseLanguageCode;
    }
  },
  
  /**
   * Get list of supported languages
   * @returns {Array<{code: string, name: string}>} List of supported languages
   */
  getSupportedLanguages() {
    return supportedLanguageCodes
      .map(code => ({
        code,
        name: this.getLanguageDisplayName(code)
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
};
